import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  SearchInput,
  Spinner,
  TextInput,
  Title,
} from '@patternfly/react-core';
import PageHeader from '@patternfly/react-component-groups/dist/dynamic/PageHeader';
import { SyncIcon } from '@patternfly/react-icons';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';
import {
  AggregateSummaryResponse,
  PlayerLookup,
  PlayerSessionRow,
  parseAggregateSummaryResponse,
  parsePlayerLookupResponse,
  parsePlayerSessionRow,
  parsePlayerSessionsResponse,
} from 'src/contracts/reporting';
import './reporting.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type SortKey = 'session_time' | 'queue_position' | 'queue_wait' | 'attempt_count' | 'result' | 'end_reason';

type SortDirection = 'asc' | 'desc';

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
};

const formatQueueWait = (value?: number | null) => {
  if (typeof value !== 'number') {
    return '—';
  }
  const totalSeconds = Math.floor(value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const formatRate = (value: number) => `${Math.round(value * 100)}%`;

const sortText = (value?: string | null) => (value || '').toLowerCase();
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isUuid = (value: string) => UUID_PATTERN.test(value.trim());

const toIsoIfSet = (value: string) => {
  if (!value.trim()) {
    return undefined;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toISOString();
};

const buildSessionParams = (
  player: PlayerLookup | null,
  fromValue: string,
  toValue: string,
  gameType: string,
  robotId: string,
) => {
  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('page_size', '100');
  if (player?.user_id) {
    params.set('user_id', player.user_id);
  } else if (player?.email) {
    params.set('email', player.email);
  }
  const fromIso = toIsoIfSet(fromValue);
  const toIso = toIsoIfSet(toValue);
  if (fromIso) {
    params.set('from', fromIso);
  }
  if (toIso) {
    params.set('to', toIso);
  }
  if (gameType.trim()) {
    params.set('game_type', gameType.trim());
  }
  if (robotId.trim()) {
    params.set('robot_id', robotId.trim());
  }
  return params;
};

const ReportingDashboard = () => {
  const authedFetch = useAuthedFetch();

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlayerLookup[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerLookup | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [gameType, setGameType] = useState('');
  const [robotId, setRobotId] = useState('');

  const [sessions, setSessions] = useState<PlayerSessionRow[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [summary, setSummary] = useState<AggregateSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [detailsBySessionId, setDetailsBySessionId] = useState<Map<string, PlayerSessionRow>>(new Map());
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('session_time');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const runTypeahead = useCallback(
    async (value: string) => {
      const nextQuery = value.trim();
      if (nextQuery.length < 2) {
        setSuggestions([]);
        return;
      }
      setSearchLoading(true);
      try {
        const isUserIdQuery = isUuid(nextQuery);
        const searchUrl = isUserIdQuery
          ? `${API_BASE}/api/reporting/players/search?user_id=${encodeURIComponent(nextQuery)}&limit=1`
          : `${API_BASE}/api/reporting/players/search?q=${encodeURIComponent(nextQuery)}&limit=8`;
        const response = await authedFetch(searchUrl);
        if (!response.ok) {
          throw new Error(`Player search failed (${response.status})`);
        }
        const raw = await response.json();
        const parsed = parsePlayerLookupResponse(raw);
        if (!parsed.ok) {
          throw new Error(parsed.err.message);
        }
        setSuggestions(parsed.value.items);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setSearchLoading(false);
      }
    },
    [authedFetch],
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void runTypeahead(query);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query, runTypeahead]);

  const runExactSearch = useCallback(async () => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      setSelectedPlayer(null);
      setSuggestions([]);
      setSessions([]);
      setSummary(null);
      return;
    }

    const isUserIdQuery = isUuid(normalizedQuery);
    const isEmailQuery = normalizedQuery.includes('@');
    const lookupUrl = isUserIdQuery
      ? `${API_BASE}/api/reporting/players/search?user_id=${encodeURIComponent(normalizedQuery)}&limit=1`
      : isEmailQuery
        ? `${API_BASE}/api/reporting/players/search?email=${encodeURIComponent(normalizedQuery.toLowerCase())}&limit=1`
        : `${API_BASE}/api/reporting/players/search?external_user_id=${encodeURIComponent(normalizedQuery)}&limit=1`;

    setSearchLoading(true);
    try {
      const response = await authedFetch(lookupUrl);
      if (!response.ok) {
        throw new Error(`Player lookup failed (${response.status})`);
      }
      const raw = await response.json();
      const parsed = parsePlayerLookupResponse(raw);
      if (!parsed.ok) {
        throw new Error(parsed.err.message);
      }
      const found = parsed.value.items[0] || null;
      if (!found) {
        setSelectedPlayer(null);
        setSessions([]);
        setSummary(null);
        setError(
          isUserIdQuery
            ? 'No player found for that user ID'
            : isEmailQuery
              ? 'No player found for that email'
              : 'No player found for that external user ID',
        );
        return;
      }
      setSelectedPlayer(found);
      setQuery(found.email || found.user_id);
      setSuggestions(parsed.value.items);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSearchLoading(false);
    }
  }, [authedFetch, query]);

  const fetchSessions = useCallback(async () => {
    if (!selectedPlayer) {
      setSessions([]);
      return;
    }

    setSessionsLoading(true);
    try {
      const params = buildSessionParams(selectedPlayer, fromValue, toValue, gameType, robotId);
      const response = await authedFetch(`${API_BASE}/api/reporting/players/sessions?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Session history load failed (${response.status})`);
      }
      const raw = await response.json();
      const parsed = parsePlayerSessionsResponse(raw);
      if (!parsed.ok) {
        throw new Error(parsed.err.message);
      }
      setSessions(parsed.value.items);
      setExpandedSessionId(null);
      setDetailsBySessionId(new Map());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSessionsLoading(false);
    }
  }, [authedFetch, selectedPlayer, fromValue, toValue, gameType, robotId]);

  const fetchSummary = useCallback(async () => {
    if (!selectedPlayer) {
      setSummary(null);
      return;
    }

    setSummaryLoading(true);
    try {
      const params = buildSessionParams(selectedPlayer, fromValue, toValue, gameType, robotId);
      const response = await authedFetch(`${API_BASE}/api/reporting/aggregates/summary?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Aggregate load failed (${response.status})`);
      }
      const raw = await response.json();
      const parsed = parseAggregateSummaryResponse(raw);
      if (!parsed.ok) {
        throw new Error(parsed.err.message);
      }
      setSummary(parsed.value);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSummaryLoading(false);
    }
  }, [authedFetch, selectedPlayer, fromValue, toValue, gameType, robotId]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchSessions(), fetchSummary()]);
  }, [fetchSessions, fetchSummary]);

  const onSelectSuggestion = (player: PlayerLookup) => {
    setSelectedPlayer(player);
    setQuery(player.email || player.display_name || player.user_id);
    setSuggestions([]);
    setError(null);
  };

  useEffect(() => {
    if (!selectedPlayer) {
      return;
    }
    void refresh();
  }, [selectedPlayer, refresh]);

  const onToggleSort = (nextKey: SortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(nextKey);
    setSortDirection('desc');
  };

  const sortedSessions = useMemo(() => {
    const copy = [...sessions];
    const factor = sortDirection === 'asc' ? 1 : -1;

    copy.sort((left, right) => {
      const leftSessionTime = left.session_started_at || left.queued_at || '';
      const rightSessionTime = right.session_started_at || right.queued_at || '';

      switch (sortKey) {
        case 'session_time':
          return sortText(leftSessionTime).localeCompare(sortText(rightSessionTime)) * factor;
        case 'queue_position':
          return ((left.initial_queue_position ?? -1) - (right.initial_queue_position ?? -1)) * factor;
        case 'queue_wait':
          return ((left.queue_wait_ms ?? -1) - (right.queue_wait_ms ?? -1)) * factor;
        case 'attempt_count':
          return (left.attempt_count - right.attempt_count) * factor;
        case 'result':
          return sortText(left.session_result?.kind).localeCompare(sortText(right.session_result?.kind)) * factor;
        case 'end_reason':
          return sortText(left.end_reason).localeCompare(sortText(right.end_reason)) * factor;
        default:
          return 0;
      }
    });

    return copy;
  }, [sessions, sortDirection, sortKey]);

  const fetchDetails = useCallback(
    async (sessionId: string) => {
      if (detailsBySessionId.has(sessionId)) {
        return;
      }
      setDetailLoadingId(sessionId);
      try {
        const response = await authedFetch(`${API_BASE}/api/reporting/players/sessions/${sessionId}`);
        if (!response.ok) {
          throw new Error(`Session detail failed (${response.status})`);
        }
        const raw = await response.json();
        const parsed = parsePlayerSessionRow(raw);
        if (!parsed.ok) {
          throw new Error(parsed.err.message);
        }
        setDetailsBySessionId((prev) => {
          const next = new Map(prev);
          next.set(sessionId, parsed.value);
          return next;
        });
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setDetailLoadingId(null);
      }
    },
    [authedFetch, detailsBySessionId],
  );

  const toggleRow = (sessionId: string) => {
    setExpandedSessionId((prev) => {
      const next = prev === sessionId ? null : sessionId;
      if (next) {
        void fetchDetails(next);
      }
      return next;
    });
  };

  return (
    <div className="reporting-page">
      <PageHeader title="Dashboard Reporting" subtitle="Player drill-down and operational aggregate metrics" />

      <Card className="reporting-search-card">
        <CardBody>
          <Flex gap={{ default: 'gapMd' }} alignItems={{ default: 'alignItemsCenter' }} flexWrap={{ default: 'wrap' }}>
            <FlexItem flex={{ default: 'flex_1' }}>
              <SearchInput
                aria-label="Search by email, user ID, or external user ID"
                placeholder="Find by email, user ID, or external user ID"
                value={query}
                onChange={(_, value) => setQuery(value)}
                onSearch={() => void runExactSearch()}
                onClear={() => {
                  setQuery('');
                  setSelectedPlayer(null);
                  setSuggestions([]);
                  setSessions([]);
                  setSummary(null);
                }}
              />
            </FlexItem>
            <FlexItem>
              <Button onClick={() => void runExactSearch()} isLoading={searchLoading}>
                Search
              </Button>
            </FlexItem>
            <FlexItem>
              <Button
                variant="secondary"
                icon={<SyncIcon />}
                onClick={() => void refresh()}
                isDisabled={!selectedPlayer}
              >
                Refresh
              </Button>
            </FlexItem>
          </Flex>

          {suggestions.length > 0 && (
            <div className="reporting-suggestions">
              {suggestions.map((player) => (
                <Button
                  key={player.user_id}
                  variant="link"
                  className="reporting-suggestion"
                  onClick={() => onSelectSuggestion(player)}
                >
                  {player.email || player.display_name || player.user_id}
                </Button>
              ))}
            </div>
          )}

          {selectedPlayer && (
            <div className="reporting-selected-player">
              <Label color="blue">{selectedPlayer.email || selectedPlayer.user_id}</Label>
              <Label color="grey">Sessions: {selectedPlayer.session_count}</Label>
              {selectedPlayer.last_session_at && (
                <Label color="grey">Last session: {formatDateTime(selectedPlayer.last_session_at)}</Label>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="reporting-filter-card">
        <CardTitle>Filters</CardTitle>
        <CardBody>
          <Grid hasGutter>
            <GridItem sm={12} md={6} lg={3}>
              <TextInput
                type="datetime-local"
                value={fromValue}
                onChange={(_, value) => setFromValue(value)}
                aria-label="From"
              />
            </GridItem>
            <GridItem sm={12} md={6} lg={3}>
              <TextInput
                type="datetime-local"
                value={toValue}
                onChange={(_, value) => setToValue(value)}
                aria-label="To"
              />
            </GridItem>
            <GridItem sm={12} md={6} lg={3}>
              <TextInput
                value={gameType}
                onChange={(_, value) => setGameType(value)}
                aria-label="Game type"
                placeholder="game_type"
              />
            </GridItem>
            <GridItem sm={12} md={6} lg={3}>
              <TextInput
                value={robotId}
                onChange={(_, value) => setRobotId(value)}
                aria-label="Robot ID"
                placeholder="robot UUID"
              />
            </GridItem>
          </Grid>
          <div className="reporting-filter-actions">
            <Button variant="secondary" onClick={() => void refresh()} isDisabled={!selectedPlayer}>
              Apply filters
            </Button>
          </div>
        </CardBody>
      </Card>

      <Grid hasGutter className="reporting-summary-grid">
        <GridItem sm={12} md={6} lg={3}>
          <Card>
            <CardTitle>Sessions Started</CardTitle>
            <CardBody>
              {summaryLoading ? (
                <Spinner size="md" />
              ) : (
                <Title headingLevel="h3">{summary?.sessions_started ?? '—'}</Title>
              )}
            </CardBody>
          </Card>
        </GridItem>
        <GridItem sm={12} md={6} lg={3}>
          <Card>
            <CardTitle>Sessions Completed</CardTitle>
            <CardBody>
              {summaryLoading ? (
                <Spinner size="md" />
              ) : (
                <Title headingLevel="h3">{summary?.sessions_completed ?? '—'}</Title>
              )}
            </CardBody>
          </Card>
        </GridItem>
        <GridItem sm={12} md={6} lg={3}>
          <Card>
            <CardTitle>Total Attempts</CardTitle>
            <CardBody>
              {summaryLoading ? (
                <Spinner size="md" />
              ) : (
                <Title headingLevel="h3">{summary?.total_attempts ?? '—'}</Title>
              )}
            </CardBody>
          </Card>
        </GridItem>
        <GridItem sm={12} md={6} lg={3}>
          <Card>
            <CardTitle>Queue Wait p95</CardTitle>
            <CardBody>
              {summaryLoading ? (
                <Spinner size="md" />
              ) : (
                <Title headingLevel="h3">{formatQueueWait(summary?.queue_wait.distribution.p95 ?? null)}</Title>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      <Card className="reporting-table-card">
        <CardTitle>
          <Flex alignItems={{ default: 'alignItemsCenter' }} justifyContent={{ default: 'justifyContentSpaceBetween' }}>
            <FlexItem>
              <Title headingLevel="h3" size="lg">
                Session History
              </Title>
            </FlexItem>
            <FlexItem>
              {summary?.metadata && (
                <Label color="grey">
                  {summary.metadata.freshness} | sample {summary.metadata.sample_size}
                </Label>
              )}
            </FlexItem>
          </Flex>
        </CardTitle>
        <CardBody>
          {error && <div className="reporting-error">{error}</div>}

          {sessionsLoading ? (
            <div className="reporting-loading">
              <Spinner size="xl" />
            </div>
          ) : sortedSessions.length === 0 ? (
            <EmptyState>
              <Title headingLevel="h4">No sessions</Title>
              <EmptyStateBody>Search for a player to view reporting history.</EmptyStateBody>
            </EmptyState>
          ) : (
            <div className="reporting-table-wrap">
              <table className="reporting-table">
                <thead>
                  <tr>
                    <th />
                    <th>
                      <Button variant="link" onClick={() => onToggleSort('session_time')}>
                        Session Time
                      </Button>
                    </th>
                    <th>
                      <Button variant="link" onClick={() => onToggleSort('queue_position')}>
                        Queue Pos
                      </Button>
                    </th>
                    <th>
                      <Button variant="link" onClick={() => onToggleSort('queue_wait')}>
                        Queue Wait
                      </Button>
                    </th>
                    <th>
                      <Button variant="link" onClick={() => onToggleSort('attempt_count')}>
                        Attempts
                      </Button>
                    </th>
                    <th>
                      <Button variant="link" onClick={() => onToggleSort('result')}>
                        Result
                      </Button>
                    </th>
                    <th>
                      <Button variant="link" onClick={() => onToggleSort('end_reason')}>
                        End Reason
                      </Button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSessions.map((session) => {
                    const detail = detailsBySessionId.get(session.session_id);
                    const expanded = expandedSessionId === session.session_id;
                    return (
                      <React.Fragment key={session.session_id}>
                        <tr>
                          <td>
                            <Button variant="link" onClick={() => toggleRow(session.session_id)}>
                              {expanded ? 'Hide' : 'Show'}
                            </Button>
                          </td>
                          <td>{formatDateTime(session.session_started_at || session.queued_at)}</td>
                          <td>{session.initial_queue_position ?? '—'}</td>
                          <td>{formatQueueWait(session.queue_wait_ms)}</td>
                          <td>{session.attempt_count}</td>
                          <td>{session.session_result?.kind || '—'}</td>
                          <td>{session.end_reason || '—'}</td>
                        </tr>
                        {expanded && (
                          <tr className="reporting-expanded-row">
                            <td colSpan={7}>
                              {detailLoadingId === session.session_id && !detail ? (
                                <Spinner size="md" />
                              ) : (
                                <div className="reporting-expanded-content">
                                  <div>
                                    <strong>Attempts</strong>
                                    <ul>
                                      {(detail?.attempts || []).map((attempt) => (
                                        <li key={attempt.id}>
                                          #{attempt.attempt_no} | {attempt.result?.kind || attempt.state} |{' '}
                                          {formatDateTime(attempt.started_at)}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                  <div>
                                    <strong>Media Links</strong>
                                    <ul>
                                      {(detail?.media || []).map((media) => (
                                        <li key={media.id}>
                                          {media.media_url ? (
                                            <a href={media.media_url} target="_blank" rel="noreferrer">
                                              {media.object_key}
                                            </a>
                                          ) : (
                                            media.object_key
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {summary && summary.attempt_result_rates.length > 0 && (
        <Card className="reporting-breakdown-card">
          <CardTitle>Attempt Result Breakdown</CardTitle>
          <CardBody>
            <div className="reporting-breakdown-row">
              {summary.attempt_result_rates.map((rate) => (
                <Label key={rate.kind} color="blue">
                  {rate.kind}: {rate.count} ({formatRate(rate.rate)})
                </Label>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default ReportingDashboard;
